import { DEG, RAD, clamp, modelById } from './constants.js';
import { fisheyeProjection } from './fisheye.js';

/** Metres of scene spanned per metre of distance, across the frame.
 *  Past 150° the chord formula stops meaning anything, so wide and panoramic
 *  optics fall back to arc length. */
export function spreadFactor(hfov) {
  return hfov >= 150 ? hfov * RAD : 2 * Math.tan(hfov * RAD / 2);
}

export function camGeom(S, cam) {
  let m = modelById(S, cam.modelId);
  if (!m) return null;
  const f = clamp(cam.focal || m.focal, m.fmin, m.fmax);
  let hfov, vfov, source = 'Calculated from active sensor width and focal length', profileId = '';
  const profiles = m.details?.opticalProfiles || [];
  const explicit = profiles.find(p => p.id === cam.profileId);
  const p = explicit || (m.details?.opticsMode === 'published' ? profiles.slice().sort((a, b) => Math.abs(a.focal - f) - Math.abs(b.focal - f))[0] : null);
  if (p) {
    hfov = p.hfov; vfov = p.vfov; profileId = p.id;
    // Each datasheet mode is an exact lens/output combination. Do not invent
    // intermediate FoV values by interpolating a lens's end points.
    m = { ...m, resW: p.resW, resH: p.resH, pano: null };
    source = `${p.status || 'estimated'} profile: ${p.name || p.id}${p.crop ? ' · ' + p.crop : ''}`;
  } else if (m.pano) {
    hfov = m.pano;
    vfov = m.pano >= 360 ? 180 : Math.min(180, m.publishedVfov || hfov * m.resH / m.resW);
    source = 'Approximate panoramic projection; dewarping and pixel density are not calibrated';
  } else {
    hfov = 2 * Math.atan(m.sensorW / (2 * f)) * DEG;
    vfov = 2 * Math.atan((m.sensorW * m.resH / m.resW) / (2 * f)) * DEG;
  }
  if (cam.corridor && m.details?.corridor) { [hfov, vfov] = [vfov, hfov]; m = { ...m, resW: m.resH, resH: m.resW }; }
  // Match recording settings precedence without allocating a recording estimate
  // for every candidate in the planner's optical calculations.
  const nativeResW = m.resW, nativeResH = m.resH;
  const resolutionScale = clamp(cam.recording?.resolutionScale ?? S.install?.recording?.resolutionScale ?? m.details?.recording?.resolutionScale ?? 1, 0.05, 1);
  if (resolutionScale !== 1) m = { ...m, resW: Math.max(1, Math.round(m.resW * resolutionScale)), resH: Math.max(1, Math.round(m.resH * resolutionScale)) };
  const geometry = { m, f: p?.focal ?? f, hfov, vfov, spread: m.pano ? spreadFactor(hfov) : 2 * Math.tan(hfov * RAD / 2), profileId, source,
    nativeResW, nativeResH, resolutionScale, projectionAspect: Math.tan(hfov * RAD / 2) / Math.tan(vfov * RAD / 2), outputAspect: m.resW / m.resH };
  geometry.fisheye = fisheyeProjection(geometry);
  return geometry;
}

/** A physical multisensor device contributes independent simultaneous frames.
 * A PTZ is evaluated only at its current pose, never as simultaneous presets. */
export function cameraViews(S, cam) {
  const m = modelById(S, cam.modelId), modules = m?.details?.sensorModules || [];
  if (!modules.length) { const g = camGeom(S, cam); return g ? [{ cam, g }] : []; }
  return modules.map(module => {
    const settings = cam.moduleSettings?.find(s => s.id === module.id) || module;
    const virtual = { ...cam, profileId: module.id, pan: (cam.pan + settings.pan + 360) % 360, tilt: clamp(cam.tilt + settings.tilt, 0, 90), focal: module.focal, moduleId: module.id };
    const model = { ...m, pano: null, details: { ...m.details, opticalProfiles: [module], sensorModules: [], opticsMode: 'published' } };
    return { cam: virtual, g: camGeom({ ...S, models: [model] }, virtual), module };
  });
}

/** Horizontal pixels per metre of scene at slant range r. */
export const pxAt = (g, r) => g.m.resW / (Math.max(r, 0.05) * g.spread);

/** The slant range at which a given pixel density is still met. */
export const rangeFor = (g, px) => g.m.resW / (px * g.spread);

/** Test the actual rectangular sensor projection in camera-local coordinates.
 * Independent azimuth/elevation limits distort the frame when a unit tilts. */
export function inCameraFrame(cam, g, x, y, targetH) {
  return cameraFrame(cam, g)(x, y, targetH);
}

/** inCameraFrame for one unchanged camera and geometry, with its per-camera
 * trigonometry computed once. The per-point arithmetic is identical. */
export function cameraFrame(cam, g) {
  const fisheye = g.fisheye === undefined ? fisheyeProjection(g) : g.fisheye;
  if (fisheye) {
    const pan=cam.pan*RAD, tilt=cam.tilt*RAD, cosPan=Math.cos(pan), sinPan=Math.sin(pan), cosTilt=Math.cos(tilt), sinTilt=Math.sin(tilt), limit=Math.cos(fisheye.halfAngle)-1e-9;
    return (x, y, targetH) => {
      const dx = x - cam.x, dy = y - cam.y, dz = targetH - cam.h, distance=Math.hypot(dx,dy,dz);
      const axial=(dx*cosPan+dy*sinPan)*cosTilt-dz*sinTilt;
      if(!(distance>1e-9) || axial/distance<limit)return false;
      if(!fisheye.cropped)return true;
      const side=dx*sinPan-dy*cosPan, up=(dx*cosPan+dy*sinPan)*sinTilt+dz*cosTilt;
      const radial=Math.hypot(side,up), r=Math.acos(clamp(axial/distance,-1,1))/fisheye.halfAngle;
      return radial<1e-9 || Math.abs(side)*r<=radial*fisheye.scale[0]+1e-9 && Math.abs(up)*r<=radial*fisheye.scale[1]+1e-9;
    };
  }
  if (g.m.pano) return (x, y, targetH) => {
    const dx = x - cam.x, dy = y - cam.y, dz = targetH - cam.h;
    const angle = Math.atan2(dy, dx) * DEG;
    const delta = Math.abs(((angle - cam.pan + 540) % 360) - 180);
    const elevation = Math.atan2(-dz, Math.hypot(dx, dy)) * DEG;
    return (g.hfov >= 360 || delta <= g.hfov / 2) && Math.abs(elevation - cam.tilt) <= g.vfov / 2;
  };
  const pan = cam.pan * RAD, tilt = cam.tilt * RAD, cosPan = Math.cos(pan), sinPan = Math.sin(pan), cosTilt = Math.cos(tilt), sinTilt = Math.sin(tilt);
  const tanH = Math.tan(g.hfov * RAD / 2), tanV = Math.tan(g.vfov * RAD / 2);
  return (x, y, targetH) => {
    const dx = x - cam.x, dy = y - cam.y, dz = targetH - cam.h;
    const horizontal = dx * cosPan + dy * sinPan;
    const depth = horizontal * cosTilt - dz * sinTilt;
    const side = dx * sinPan - dy * cosPan;
    const up = horizontal * sinTilt + dz * cosTilt;
    return depth > 0 && Math.abs(side) <= depth * tanH + 1e-9 &&
      Math.abs(up) <= depth * tanV + 1e-9;
  };
}

export function camRanges(S, cam) {
  const g = camGeom(S, cam);
  if (!g) return null;
  const t = S.opt.t;
  return {
    g,
    detect: rangeFor(g, t.detect),
    observe: rangeFor(g, t.observe),
    recognise: rangeFor(g, t.recognise),
    identify: rangeFor(g, t.identify),
    plate: rangeFor(g, t.plate)
  };
}

/** Where the vertical field lands on the ground: the near edge is the blind
 *  ring underneath the camera, the far edge is where the frame runs out (or the
 *  horizon, if the top of frame is at or above it). */
export function groundBand(cam, g, targetH) {
  const dh = cam.h - targetH;
  if (dh <= 0.02) return { near: 0, far: Infinity };
  const top = cam.tilt - g.vfov / 2, bot = cam.tilt + g.vfov / 2;
  const far = top <= 0.5 ? Infinity : dh / Math.tan(top * RAD);
  const near = bot >= 89.5 ? 0 : Math.max(0, dh / Math.tan(bot * RAD));
  return { near, far };
}

/** The tilt that centres the vertical field on a ground distance. */
export function tiltFor(h, targetH, d) {
  return clamp(Math.atan2(Math.max(h - targetH, 0.1), Math.max(d, 0.3)) * DEG, 0, 75);
}
